import type { OptimizationModel } from '@/shared/generated'
import type { AxiosError } from 'axios'
import type { TFunction } from 'i18next'
import type { MouseEvent } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { any, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { clarify } from '@/shared/components/ui/clarify'
import { confirm } from '@/shared/components/ui/confirmer'
import { OptimizationStatus } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import { successActionMessage } from '@/shared/tools/message'

import {
	useCommitOptimizationMutation,
	useDeleteExportOptimizationMutation,
	useLockOptimizationMutation,
	useResetOptimizationMutation,
	useStartOptimizationMutation,
	useStopOptimizationMutation,
	useUnlockOptimizationMutation,
	useUpdateOptimizationFinalFieldsMutation,
	useUpdateOptimizationWithDiffsMutation,
} from '../api/mutations'
import { useOptimizationQuery } from '../api/queries'

const skipEvent = (event: MouseEvent<HTMLButtonElement>) =>
	event && event.stopPropagation()
const showSuccessMessage = (action: string, id: string, t: TFunction) => () =>
	toast.success(successActionMessage('Optimization', action, t), { id })

type OptimizationController = {
	/**
	 * Current optimization data
	 */
	optimization: OptimizationModel

	isProcessAction: boolean
	isOptimizationLoading: boolean

	/**
	 * Start optimization process
	 * move optimization to status `new > started|scheduled`
	 */
	start(): void

	/**
	 * Commit changes to optimization
	 * Move optimization to status `pending > updating > new`
	 */
	commit(): void

	/**
	 * Create and start optimization process
	 * Move optimization to status `* > new > started|scheduled`
	 */
	createAndStart(): void

	create(): void
	/**
	 * Stop optimization process
	 * Move optimization to status `started|scheduled > canceled|new|scheduled`
	 */
	stop(): void

	/**
	 * Apply optimization
	 * Move optimization to status `finished > locked`
	 */
	lock(): void

	/**
	 * Unlock optimization
	 * Move optimization to status `locked > finished`
	 */
	unlock(): void
}
export default function useOptimization(
	optimizationGroupId: string
): OptimizationController {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const { data: optimization, isLoading: isOptimizationLoading } =
		useOptimizationQuery(optimizationGroupId)
	const reset = useResetOptimizationMutation()
	const commit = useCommitOptimizationMutation()
	const diffUpdate = useUpdateOptimizationWithDiffsMutation(optimizationGroupId)
	const finalUpdate =
		useUpdateOptimizationFinalFieldsMutation(optimizationGroupId)
	const start = useStartOptimizationMutation()
	const stop = useStopOptimizationMutation()
	const lock = useLockOptimizationMutation()
	const unlock = useUnlockOptimizationMutation()
	const deleteExport = useDeleteExportOptimizationMutation()
	const isProcessAction = any(prop('isPending'), [
		deleteExport,
		reset,
		diffUpdate,
		finalUpdate,
		start,
		stop,
		lock,
		unlock,
		commit,
	])
	const handleActionError = (
		error: AxiosError<{
			error: string
		}>
	) => {
		// Check if error is from backend with error message
		if (error?.response?.data?.error) {
			toast.error(error?.response?.data?.error, { id: optimizationGroupId })
			queryClient.invalidateQueries({
				queryKey: ['optimization', optimizationGroupId],
			})
		} else {
			toast.error(t('fatal_error_title'), { id: optimizationGroupId })
		}
	}
	const startOptimization = async () => {
		if (optimization?.status === OptimizationStatus.Failed) {
			ampli.repricingRestart()
		} else {
			ampli.repricingStart()
		}

		return start.mutateAsync(optimizationGroupId)
	}
	const commitOptimization = () => {
		ampli.repricingCommit()

		return commit.mutateAsync(optimizationGroupId)
	}
	const createOptimization = () => reset.mutateAsync(optimizationGroupId)
	const stopOptimization = () => {
		ampli.repricingStop()

		return stop.mutateAsync(optimizationGroupId)
	}
	const unlockOptimization = async () => {
		ampli.repricingCancelRepricing()
		try {
			const answer = await clarify({
				title: t('opt.confirm.revert_applied.title'),
				description: t('opt.confirm.revert_applied.description'),
				actions: [
					{
						actionId: 'keep_optimized_prices',
						ActionProps: { variant: 'secondary' },
						actionText: t(
							'opt.confirm.revert_applied.action.keep_optimized_prices'
						),
					},
					{
						actionId: 'revert_full_state',
						ActionProps: { variant: 'primary' },
						actionText: t(
							'opt.confirm.revert_applied.action.revert_full_state'
						),
					},
				],
			})
			if (!answer || answer === 'close') {
				return
			}
			if (answer === 'revert_full_state') {
				await deleteExport.mutateAsync(optimizationGroupId)
			}
			await unlock.mutateAsync(optimizationGroupId)
			toast.success(successActionMessage('Optimization', 'unlocked', t), {
				id: optimizationGroupId,
			})
		} catch (error) {
			handleActionError(
				error as AxiosError<{
					error: string
				}>
			)
		}
	}
	const lockOptimization = async () => {
		try {
			if (optimization!.is_exported) {
				const answer = await confirm({
					title: t('optimization_export_again_title'),
					description: t('optimization_export_again_message'),
					actionText: t('general_apply'),
					cancelText: t('general_cancel'),
				})
				if (answer) {
					await lock.mutateAsync(optimizationGroupId)
				}
			} else {
				await lock.mutateAsync(optimizationGroupId)
				toast.success(successActionMessage('Optimization', 'applied', t), {
					id: optimizationGroupId,
				})
			}
		} catch (error) {
			handleActionError(
				error as AxiosError<{
					error: string
				}>
			)
		}
	}
	const actionStart = () =>
		startOptimization()
			.then(showSuccessMessage('started', optimizationGroupId, t))
			.catch(handleActionError)
	const actionCommit = () =>
		commitOptimization()
			.then(showSuccessMessage('changed', optimizationGroupId, t))
			.catch(handleActionError)
	const actionCreateAndStart = () =>
		createOptimization().then(actionStart).catch(handleActionError)
	const actionStop = () =>
		stopOptimization()
			.then(showSuccessMessage('stopped', optimizationGroupId, t))
			.catch(handleActionError)

	return {
		optimization: optimization as OptimizationModel,
		isProcessAction,
		isOptimizationLoading,
		start: pipe([skipEvent, actionStart]),
		commit: pipe([skipEvent, actionCommit]),
		create: pipe([skipEvent, createOptimization]),
		createAndStart: pipe([skipEvent, actionCreateAndStart]),
		stop: pipe([skipEvent, actionStop]),
		lock: pipe([skipEvent, lockOptimization]),
		unlock: pipe([skipEvent, unlockOptimization]),
	}
}
